import { useCallback, useEffect, useState } from "react";
import { useRevalidator } from "react-router-dom";

import { useUser } from "routes/dataroutes/UserData";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { useActiveCoupons } from "routes/data-routes/billing-data/coupons/active-coupons";
import ActiveSubscription from "../../../types/ActiveSubscription";
import { FetcherReloadFunc } from "../useFetcherData";


const MAX_RETRY_COUNT = 5;
const TIMEOUT_MS = 3000;

/**
 * @deprecated should use the new one below
 */
export function useSubscriptionSynchronizerOld() {
  const user = useUser();
  const { activeSubscription, reloadActiveSubscription } = useActiveSubscription();
  const { reloadPricingOptions } = usePricingOptions();
  const { reloadActiveCoupons } = useActiveCoupons();
  const revalidator = useRevalidator();

  const [ count, setCount ] = useState(0);

  useEffect(() => {
    if (user && activeSubscription) {
      if ((user.planSlug === activeSubscription.planSlug) && (!activeSubscription.lastRenewalAttempt)) {
        if (count > 0) {
          // plan just synced after a mismatch — reload fetcher-based data
          // that route revalidation doesn't cover
          reloadActiveSubscription(false);
          reloadPricingOptions(false);
          reloadActiveCoupons(false);
        }
        setCount(0);
      } else if (count < MAX_RETRY_COUNT) {
        const timeoutId = setTimeout(() => {
          setCount(count => count + 1);
          revalidator.revalidate();
        }, TIMEOUT_MS);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [ user, activeSubscription, revalidator, count, setCount, reloadActiveSubscription, reloadPricingOptions, reloadActiveCoupons ]);
}


export function useSubscriptionSynchronizer({
  subscription,
  reloadSubscription,
  shouldAwaitProvisioning = false
}: {
  subscription: ActiveSubscription | null | undefined;
  reloadSubscription: FetcherReloadFunc | undefined;
  shouldAwaitProvisioning?: boolean;
}) {

  const [ pollCount, setPollCount ] = useState(0);


  useEffect(() => {
    const provisioningPending = shouldAwaitProvisioning && !subscription;
    if (subscription?.stalePlanSlug || subscription?.lastRenewalAttempt || provisioningPending) {
      // if stale slug is set, that means a change has not been fully processed yet by the billing services and the
      // database and billing are still out of sync.
      // same for when the renewal attempt is not cleared yet.

      if (pollCount < MAX_RETRY_COUNT) {
        // poll for changes after a bit of delay
        const timeoutId = setTimeout(() => {
          setPollCount(c => c + 1);
          reloadSubscription?.(false);
        }, TIMEOUT_MS);

        return () => clearTimeout(timeoutId);
      } else {
        // TODO should we inform the user somehow that we're still out of sync?
      }
    } else {
      setPollCount(0);
    }
  }, [ subscription, pollCount, reloadSubscription, shouldAwaitProvisioning ]);

  const provisioningPending = shouldAwaitProvisioning && !subscription;
  const retrySubscriptionSync = useCallback(() => {
    setPollCount(0);
    reloadSubscription?.(false);
  }, [ reloadSubscription ]);
  return {
    subscriptionSyncPending: provisioningPending && pollCount < MAX_RETRY_COUNT,
    subscriptionSyncTimedOut: provisioningPending && pollCount >= MAX_RETRY_COUNT,
    retrySubscriptionSync
  };

}
